import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useTutorial } from './context/TutorialContext'

const TutorialDetail = () => {
  const { activeTutorial, openFloatingPlayer, closeTutorial } = useTutorial()
  const [overlayVisible, setOverlayVisible] = useState(true)
  const cardRef = useRef(null)

  useEffect(() => {
    setOverlayVisible(true)
  }, [activeTutorial])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        closeTutorial()
      }
    }

    const handleEsc = (e) => {
      if (e.key === 'Escape') closeTutorial()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [closeTutorial])

  if (!activeTutorial) return null

  const handlePlayHere = () => {
    setOverlayVisible(false)
  }

  const handleFloat = () => {
    openFloatingPlayer(activeTutorial)
    closeTutorial()
  }

  return (
    <OverlayBackground>
      <DetailCard ref={cardRef}>
        <Header>
          <Title>{activeTutorial.title}</Title>
          <CloseBtn onClick={closeTutorial}>×</CloseBtn>
        </Header>

        <Description>{activeTutorial.description}</Description>

        <VideoWrapper>
          <div
            className="iframe-wrapper"
            dangerouslySetInnerHTML={{ __html: activeTutorial.iframe }}
          />

          {overlayVisible && (
            <Overlay>
              <OverlayContent>
                <h4>Choose how to view this tutorial</h4>
                <Buttons>
                  <button className="primary" onClick={handlePlayHere}>
                    ▶ Play here
                  </button>
                  <button className="secondary" onClick={handleFloat}>
                    ⤴ Open in floating mode
                  </button>
                </Buttons>
              </OverlayContent>
            </Overlay>
          )}
        </VideoWrapper>
      </DetailCard>
    </OverlayBackground>
  )
}

export default TutorialDetail

const OverlayBackground = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 18, 24, 0.65);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999 !important;
  animation: fadeIn 0.25s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.97); }
    to { opacity: 1; transform: scale(1); }
  }
`

const DetailCard = styled.div`
  background: #fff;
  padding: 28px 32px;
  border-radius: 18px;
  width: 90%;
  max-width: 850px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  animation: cardIn 0.3s ease;

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    padding: 20px;
    width: 95%;
    max-height: 95vh;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.3px;
`

const Description = styled.p`
  margin: 8px 0 24px;
  color: #555;
  font-size: 15.5px;
  line-height: 1.5;
  max-width: 90%;
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 26px;
  cursor: pointer;
  color: #999;
  transition: color 0.2s ease;
  &:hover {
    color: #111;
  }
`

const VideoWrapper = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.1);
  background: #000;

  .iframe-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  .iframe-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  text-align: center;
  z-index: 10;
`

const OverlayContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: rgba(0, 0, 0, 0.65);
  padding: 28px 36px;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);

  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;

  button {
    font-size: 15px;
    font-weight: 600;
    padding: 10px 22px;
    border-radius: 8px;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
  }

  @media (max-width: 500px) {
    flex-direction: column;
    width: 100%;

    button {
      width: 100%;
    }
  }

  .primary {
    background: #fff;
    color: #111;
    box-shadow: 0 2px 6px rgba(255, 255, 255, 0.2);
    &:hover {
      background: #f4f4f4;
    }
  }

  .secondary {
    background: #1a73e8;
    color: #fff;
    box-shadow: 0 2px 6px rgba(26, 115, 232, 0.3);
    &:hover {
      background: #1666cc;
    }
  }
`
